if (!customElements.get('testimonial-carousel')) {
  customElements.define(
    'testimonial-carousel',
    class TestimonialCarousel extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.track = this.querySelector('.testimonial-carousel__track');
        this.previousButton = this.querySelector('[data-previous]');
        this.nextButton = this.querySelector('[data-next]');
        this.controls = this.querySelector('[data-controls]');
        this.autoplayButton = this.querySelector('[data-autoplay-toggle]');

        if (!this.track || !this.previousButton || !this.nextButton || !this.controls) return;

        this.autoplayEnabled = this.dataset.autoplay === 'true';
        this.autoplayDelay = Number.parseInt(this.dataset.autoplaySpeed, 10) || 5000;
        this.designMode = this.dataset.designMode === 'true' || Boolean(window.Shopify && Shopify.designMode);
        this.userPaused = false;
        this.interactionPaused = false;
        this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onPreviousClick = () => this.move(-1, false);
        this.onNextClick = () => this.move(1, false);
        this.onAutoplayClick = () => this.toggleAutoplay();
        this.onScroll = () => this.requestUpdate();
        this.onResize = () => this.requestUpdate();
        this.onMouseEnter = () => this.setInteractionPaused(true);
        this.onMouseLeave = () => this.setInteractionPaused(false);
        this.onFocusIn = () => this.setInteractionPaused(true);
        this.onFocusOut = (event) => {
          if (!this.contains(event.relatedTarget)) this.setInteractionPaused(false);
        };
        this.onPointerDown = () => this.setInteractionPaused(true);
        this.onPointerUp = () => this.setInteractionPaused(false);
        this.onVisibilityChange = () => this.refreshAutoplay();
        this.onReducedMotionChange = () => {
          this.updateAutoplayButton();
          this.refreshAutoplay();
        };
        this.onBlockSelect = (event) => {
          const item = event.target.closest('.testimonial-carousel__item');
          if (!item || !this.contains(item)) return;
          this.setInteractionPaused(true);
          item.scrollIntoView({ behavior: this.getScrollBehaviour(), block: 'nearest', inline: 'center' });
        };
        this.onBlockDeselect = () => this.setInteractionPaused(false);

        this.previousButton.addEventListener('click', this.onPreviousClick);
        this.nextButton.addEventListener('click', this.onNextClick);
        this.autoplayButton?.addEventListener('click', this.onAutoplayClick);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.addEventListener('mouseenter', this.onMouseEnter);
        this.addEventListener('mouseleave', this.onMouseLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        this.track.addEventListener('pointerup', this.onPointerUp, { passive: true });
        this.track.addEventListener('pointercancel', this.onPointerUp, { passive: true });
        this.addEventListener('shopify:block:select', this.onBlockSelect);
        this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('resize', this.onResize);

        if (typeof this.reducedMotionQuery.addEventListener === 'function') {
          this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);
        } else {
          this.reducedMotionQuery.addListener(this.onReducedMotionChange);
        }

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.requestUpdate());
          this.resizeObserver.observe(this.track);
        }

        this.updateControls();
        this.updateAutoplayButton();
        this.refreshAutoplay();
      }

      disconnectedCallback() {
        if (!this.initialised || !this.track) return;

        window.cancelAnimationFrame(this.updateFrame);
        window.clearTimeout(this.autoplayTimer);
        this.resizeObserver?.disconnect();

        this.previousButton?.removeEventListener('click', this.onPreviousClick);
        this.nextButton?.removeEventListener('click', this.onNextClick);
        this.autoplayButton?.removeEventListener('click', this.onAutoplayClick);
        this.track.removeEventListener('scroll', this.onScroll);
        this.removeEventListener('mouseenter', this.onMouseEnter);
        this.removeEventListener('mouseleave', this.onMouseLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        this.track.removeEventListener('pointerdown', this.onPointerDown);
        this.track.removeEventListener('pointerup', this.onPointerUp);
        this.track.removeEventListener('pointercancel', this.onPointerUp);
        this.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        window.removeEventListener('resize', this.onResize);

        if (typeof this.reducedMotionQuery?.removeEventListener === 'function') {
          this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
        } else {
          this.reducedMotionQuery?.removeListener(this.onReducedMotionChange);
        }
      }

      getScrollBehaviour() {
        return this.reducedMotionQuery.matches ? 'auto' : 'smooth';
      }

      getScrollStep() {
        const firstItem = this.track.querySelector('.testimonial-carousel__item');
        if (!firstItem) return this.track.clientWidth;

        const trackStyles = window.getComputedStyle(this.track);
        const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap || 0);
        return firstItem.getBoundingClientRect().width + gap;
      }

      getMaxScroll() {
        return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      }

      hasOverflow() {
        return this.getMaxScroll() > 2;
      }

      move(direction, fromAutoplay) {
        const maxScroll = this.getMaxScroll();
        if (maxScroll <= 2) return;

        let target = this.track.scrollLeft + this.getScrollStep() * direction;

        if (fromAutoplay && direction > 0 && this.track.scrollLeft >= maxScroll - 2) {
          target = 0;
        } else if (fromAutoplay && direction < 0 && this.track.scrollLeft <= 2) {
          target = maxScroll;
        } else {
          target = Math.max(0, Math.min(maxScroll, target));
        }

        this.track.scrollTo({ left: target, behavior: this.getScrollBehaviour() });

        if (!fromAutoplay) this.refreshAutoplay();
      }

      toggleAutoplay() {
        this.userPaused = !this.userPaused;
        this.updateAutoplayButton();
        this.refreshAutoplay();
      }

      setInteractionPaused(paused) {
        if (this.interactionPaused === paused) return;
        this.interactionPaused = paused;
        this.refreshAutoplay();
      }

      canAutoplay() {
        return (
          this.autoplayEnabled &&
          !this.designMode &&
          !this.userPaused &&
          !this.interactionPaused &&
          !this.reducedMotionQuery.matches &&
          !document.hidden &&
          this.hasOverflow()
        );
      }

      refreshAutoplay() {
        window.clearTimeout(this.autoplayTimer);
        if (!this.canAutoplay()) return;

        this.autoplayTimer = window.setTimeout(() => {
          this.move(1, true);
          this.refreshAutoplay();
        }, this.autoplayDelay);
      }

      updateAutoplayButton() {
        if (!this.autoplayButton) return;

        const unavailable = this.reducedMotionQuery.matches || !this.hasOverflow();
        const paused = this.userPaused || unavailable;
        const pauseLabel = this.autoplayButton.dataset.pauseLabel || 'Pause automatic rotation';
        const playLabel = this.autoplayButton.dataset.playLabel || 'Resume automatic rotation';

        this.autoplayButton.dataset.paused = paused ? 'true' : 'false';
        this.autoplayButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
        this.autoplayButton.setAttribute('aria-label', paused ? playLabel : pauseLabel);
        this.autoplayButton.disabled = unavailable;
      }

      requestUpdate() {
        window.cancelAnimationFrame(this.updateFrame);
        this.updateFrame = window.requestAnimationFrame(() => this.updateControls());
      }

      updateControls() {
        const maxScroll = this.getMaxScroll();
        const hasOverflow = maxScroll > 2;

        this.controls.hidden = !hasOverflow;
        this.previousButton.disabled = !hasOverflow || this.track.scrollLeft <= 2;
        this.nextButton.disabled = !hasOverflow || this.track.scrollLeft >= maxScroll - 2;
        this.updateAutoplayButton();

        if (!hasOverflow) {
          window.clearTimeout(this.autoplayTimer);
        } else {
          this.refreshAutoplay();
        }
      }
    }
  );
}
